import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import DoneIcon from '@mui/icons-material/Done'
import { Box, Container, Grid, Paper, Typography } from '@mui/material'
import { compact, includes } from 'lodash'
import moment from 'moment'

import { DigitalCertificate, Pdf, Progress } from '../api'
import { ACCEPTOR_ROLES, REACT_APP_DOMAIN } from '../common/constants'
import AppSpinner from '../components/AppSpinner'
import ButtonBox from '../components/ButtonBox'
import CarDataTable from '../components/CarDataTable'
import Ratings from '../components/Ratings'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import useAlert from '../hooks/useAlert'
import useAuth from '../hooks/useAuth'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import { CarIcon, DetailsIcon, EditIcon, PrinterIcon } from '../icons'

function ControlsSummary() {
  const [digitalCertificate, setDigitalCertificate] = useState({})
  const [progress, setProgress] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isApproving, setIsApproving] = useState(false)
  const [isPdfReady, setIsPdfLoading] = useState(false)
  const { auth } = useAuth()
  const { setAlert } = useAlert()
  const axiosPrivate = useAxiosPrivate()
  const { id: digitalCertificateId } = useParams()
  const { t } = useTranslation()

  const isRefactory = auth.refactory === 'true'
  const isCompleted = progress === 100
  const iconFill = isCompleted ? '#000' : '#c0bcbb'

  const { basicData = {}, rating = {} } = digitalCertificate ?? {}
  const {
    make = '',
    model = '',
    version = '',
    controlRating = 0,
    imageUrl = '',
    controlStatus = '',
    controlProgress = 0,
    mileage = '',
    color = '',
    vin = '',
    registrationNumber = '',
    manufacturingYear = '',
    firstRegistrationDate = null,
  } = basicData ?? {}

  useEffect(() => {
    Progress.getProgress(axiosPrivate, digitalCertificateId)
      .then(({ data }) => {
        setProgress(data.totalProgress)
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))

    DigitalCertificate.getCertificateData(axiosPrivate, digitalCertificateId)
      .then(({ data }) => {
        setIsLoading(false)
        setDigitalCertificate(data)
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, digitalCertificateId])

  const getStaticPDF = () => {
    window.open(
      `${REACT_APP_DOMAIN}/Content/Files/Reguly-oceny-pojazdu.pdf`,
      '_blank',
    )
  }

  const approveCertificate = () => {
    setIsApproving(true)
    DigitalCertificate.approveCertificate(axiosPrivate, digitalCertificateId)
      .then(() => {
        setDigitalCertificate({
          ...digitalCertificate,
          basicData: {
            ...basicData,
            controlStatus: 'Confirmed',
          },
        })
      })
      .catch((e) => {
        // eslint-disable-next-line no-shadow, no-console
        console.error(e)
        if (
          includes(
            [
              'BodyMissingImages',
              'InteriorMissingImages',
              'TechnicalMissingImages',
            ],
            e.response.data.data,
          )
        ) {
          setAlert(e.response.data.message, 'warning')
        }
      })
      .finally(() => {
        setIsApproving(false)
      })
  }

  const getPdf = (v) => {
    setIsPdfLoading(true)
    Pdf.getPdf(axiosPrivate, digitalCertificateId, v)
      .then((res) => {
        setIsPdfLoading(false)
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: 'application/pdf' }),
        )
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `${make}-${model}-${manufacturingYear}.pdf`,
        )
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }

  return isLoading ? (
    <AppSpinner />
  ) : (
    <Container maxWidth="xl">
      <Section>
        <SectionHeader title={t('summaryTitle')} />
        <Paper elevation={0}>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6} md={4}>
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%',
                }}
              >
                <Box>
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {`${make ?? ''} ${model ?? ''}`}
                  </Typography>
                  <Typography>{version}</Typography>
                </Box>
                <Ratings
                  big
                  label={t('summaryRatingLabel')}
                  orientation="vertical"
                  score={controlRating ?? 0}
                  starSize={48}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h3">{t('digital.rating')}</Typography>
                <Ratings label={t('summaryLabel1')} score={rating?.body ?? 0} />
                <Ratings
                  label={t('summaryLabel2')}
                  score={rating?.interior ?? 0}
                />
                <Ratings
                  label={t('summaryLabel3')}
                  score={rating?.equipment ?? 0}
                />
                <Ratings label={t('tires')} score={rating?.tyres ?? 0} />
                <Ratings
                  label={t('summaryLabel5')}
                  score={rating?.technical ?? 0}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ position: 'relative', m: 3 }}>
                <img
                  src={imageUrl ?? ''}
                  alt=""
                  style={{ width: '100%', height: 'auto' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Section>
      <ButtonBox
        flexDirection="column"
        buttons={compact([
          {
            label: 'Reguły oceny pojazdu',
            props: {
              onClick: getStaticPDF,
            },
          },
          {
            label: t('editBtnLabel'),
            title:
              controlStatus === 'Confirmed'
                ? 'Kontrola zatwierdzona. Nie można edytować'
                : null,
            props: {
              variant: 'outlined',
              color: 'primary',
              startIcon: <EditIcon />,
              component: Link,
              to: `/controls/${digitalCertificateId}/edit`,
              disabled: controlStatus === 'Confirmed',
            },
          },
          includes(ACCEPTOR_ROLES, auth.user.role) &&
            controlProgress === 100 &&
            !isRefactory && {
              label:
                controlStatus === 'Confirmed'
                  ? 'Kontrola zatwierdzona'
                  : 'Zatwierdź kontrolę',
              props: {
                variant: 'contained',
                color: 'primary',
                startIcon: controlStatus === 'Confirmed' ? <DoneIcon /> : null,
                disabled: isApproving || controlStatus === 'Confirmed',
                onClick: approveCertificate,
              },
            },
        ])}
      />
      <Section>
        <SectionHeader
          title={t('summarySectionTitle')}
          small
          titleIcon={CarIcon}
        />
        <Grid container spacing={5}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0}>
              <CarDataTable
                data={[
                  {
                    label: t('mileage'),
                    value: mileage ?? '',
                  },
                  {
                    label: t('color'),
                    value: color ?? '',
                  },
                  { label: 'VIN', value: vin ?? '' },
                ]}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0}>
              <CarDataTable
                data={[
                  {
                    label: t('regNo'),
                    value: registrationNumber ?? '',
                  },
                  {
                    label: t('productionYear'),
                    value: manufacturingYear ?? '',
                  },
                  {
                    label: t('firstRegistrationDate'),
                    value: firstRegistrationDate
                      ? moment(firstRegistrationDate).format('DD-MM-YYYY')
                      : '',
                  },
                ]}
              />
            </Paper>
          </Grid>
        </Grid>
      </Section>
      <ButtonBox
        flexDirection="column"
        buttons={[
          {
            label: t('printBtnLabel'),
            props: {
              variant: 'outlined',
              disabled: !isCompleted,
              color: 'primary',
              startIcon: <PrinterIcon fill={iconFill} />,
              loading: isPdfReady,
              onClick: () => getPdf(),
            },
          },
          {
            label: t('summaryBtnLabel1'),
            props: {
              variant: 'outlined',
              disabled: !isCompleted,
              color: 'primary',
              startIcon: <DetailsIcon fill={iconFill} />,
              component: Link,
              to: `/controls/${digitalCertificateId}/digital`,
            },
          },
          {
            label: t('backToListBtnLabel'),
            props: {
              variant: 'outlined',
              color: 'primary',
              component: Link,
              to: '/controls',
              reloadDocument: true,
            },
          },
          {
            label: t('summaryBtnLabel2'),
            props: {
              variant: 'contained',
              color: 'primary',
              component: Link,
              to: '/controls/new/car',
            },
          },
        ]}
      />
    </Container>
  )
}

export default ControlsSummary
